import { useCallback, useMemo, useState } from "react";

import { DataTable } from "@/shared/components/DataTable/DataTable";
import SummaryMetricCard from "@/shared/components/SummaryMetricCard";
import { B2C_FEATURE_FLAGS } from "@/shared/config/header.config";
import { showToast } from "@/shared/lib/toast";
import { closeModal, openModal } from "@/shared/redux/reducers/uiSlice";
import { useAppDispatch } from "@/shared/redux/store/hooks";
import { textFormatter } from "@/shared/utils/textFormatter";
import { CheckCircle2, Clock3, FileText, Wallet } from "lucide-react";

import { App_Url_New } from "@/shared/common/static";
import {
  setCommissionInsurer,
  setCommissionRecords as listReducer,
  setCommissionStatuses,
} from "@/shared/redux/reducers/commission-ledger";
import { usePosterReducers } from "@/shared/redux/store/useGetData";
import { formatGroupedAmount } from "@/shared/utils/amountFormat";
import { Status } from "../Policies/components/Status";
import GenerateInvoicesModal from "./components/Modal/GenerateInvoicesModal";
import InvoicePrintModal from "./components/Modal/InvoicePrintModal";
import { INVOICE_LOCALISATION } from "./constants/localisation";
import { getInvoiceDetail } from "./services/mockData";
import type { ApiInvoiceRow, GenerateInvoiceFormValues, InvoiceRecord } from "./types";

const formatCurrency = (amount: number) =>
  `${INVOICE_LOCALISATION.currencySymbol} ${formatGroupedAmount(amount || 0, {
    locale: INVOICE_LOCALISATION.numberLocale,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    fallback: "0.00",
  })}`;

const toNumber = (value: unknown, fallback = 0) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

const formatDate = (dateValue?: string) => {
  if (!dateValue) return "-";

  const parsed = new Date(dateValue);
  if (Number.isNaN(parsed.getTime())) return "-";

  return parsed.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatPeriod = (from?: string, to?: string) => {
  const label = (value?: string) => {
    if (!value) return "";

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return "";

    return parsed.toLocaleDateString("en-GB", { month: "short", year: "numeric" });
  };

  const startLabel = label(from);
  const endLabel = label(to);

  if (!startLabel && !endLabel) return "-";
  if (!startLabel || startLabel === endLabel) return endLabel || startLabel;
  if (!endLabel) return startLabel;

  return `${startLabel} - ${endLabel}`;
};

const ALLOWED_STATUSES = new Set(["PAID", "UNBILLED", "RECONCILED", "INVOICED"]);

const mapApiRowToInvoiceRecord = (row: ApiInvoiceRow): InvoiceRecord => {
  const normalizedStatus = String(row.status ?? "").toUpperCase();
  const status = (
    ALLOWED_STATUSES.has(normalizedStatus) ? normalizedStatus : "UNBILLED"
  ) as InvoiceRecord["status"];

  const periodFrom = row.period_from || row.issue_date || "";
  const invoiceAmount = toNumber(row.invoice_amount ?? row.commission);
  const totalCollected =
    row.total_collected !== undefined
      ? toNumber(row.total_collected)
      : status === "PAID"
        ? invoiceAmount
        : 0;

  return {
    id: row.id ?? "",
    invoiceNo: String(row.invoice_no ?? row.invoice ?? "-"),
    insurerId: row.insurer_id ?? "",
    insurer: row.insurer || "-",
    period: formatPeriod(periodFrom, row.period_to || periodFrom),
    periodFrom,
    invoiceAmount,
    totalCollected,
    outstanding:
      row.outstanding !== undefined
        ? toNumber(row.outstanding)
        : Math.max(invoiceAmount - totalCollected, 0),
    status,
    dueDate: row.due_date || "",
    policyNo: String(row.policy_no ?? "-"),
    product: row.product || "-",
    customer: row.customer || "-",
  };
};

export default function InvoicesPage() {
  const dispatch = useAppDispatch();

  const {
    commissionLedger: { commissionRecords, statuses, insurers },
  } = usePosterReducers();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const records = useMemo(
    () =>
      (commissionRecords?.data || []).map((row) => mapApiRowToInvoiceRecord(row as ApiInvoiceRow)),
    [commissionRecords?.data]
  );

  const summary = useMemo(
    () => ({
      entries: commissionRecords?.total || records.length,
      invoiced: records.reduce((sum, row) => sum + row.invoiceAmount, 0),
      collected: records.reduce((sum, row) => sum + row.totalCollected, 0),
      outstanding: records.reduce((sum, row) => sum + row.outstanding, 0),
    }),
    [records, commissionRecords?.total]
  );

  const handleGenerateInvoice = useCallback(
    async (values: GenerateInvoiceFormValues, lineCount: number) => {
      setIsSubmitting(true);

      try {
        showToast(
          `Draft for ${values.period} (${lineCount} line(s)) will be saved once the API is live.`,
          "info"
        );
        dispatch(closeModal());
      } catch (error) {
        console.error("Save invoice draft error:", error);
        showToast("Failed to save invoice draft", "error");
      } finally {
        setIsSubmitting(false);
      }
    },
    [dispatch]
  );

  const openGenerateInvoiceModal = useCallback(() => {
    dispatch(
      openModal({
        title: "Generate Invoice",
        children: (
          <GenerateInvoicesModal
            isSubmitting={isSubmitting}
            onSubmit={handleGenerateInvoice}
            onCancel={() => dispatch(closeModal())}
          />
        ),
        className: "max-w-2xl overflow-visible",
        status: "add",
      })
    );
  }, [dispatch, handleGenerateInvoice, isSubmitting]);

  const openInvoicePrintModal = useCallback(
    (row: InvoiceRecord) => {
      const hasInvoiceNo = Boolean(row.invoiceNo) && row.invoiceNo !== "-";
      const apiRow = (commissionRecords?.data || []).find(
        (entry) => String((entry as ApiInvoiceRow).id ?? "") === String(row.id)
      ) as ApiInvoiceRow | undefined;

      dispatch(
        openModal({
          title: hasInvoiceNo ? `Invoice ${row.invoiceNo}` : "Invoice",
          children: (
            <InvoicePrintModal
              detail={hasInvoiceNo ? getInvoiceDetail(apiRow) : null}
              emptyMessage={
                hasInvoiceNo
                  ? `Invoice ${row.invoiceNo} could not be loaded. Please refresh and try again.`
                  : `This ${row.insurer !== "-" ? row.insurer : "commission"} entry has not been invoiced yet. Use "Generate Invoice" to raise one for this period.`
              }
              onClose={() => dispatch(closeModal())}
            />
          ),
          className: "max-w-4xl",
          status: "modal",
        })
      );
    },
    [dispatch, commissionRecords?.data]
  );

  const summaryCards = useMemo(
    () => [
      {
        label: "Total Invoiced",
        value: formatCurrency(summary.invoiced),
        theme: "primary" as const,
        icon: <FileText className="h-5 w-5" />,
      },
      {
        label: "Collected",
        value: formatCurrency(summary.collected),
        theme: "success" as const,
        icon: <CheckCircle2 className="h-5 w-5" />,
      },
      {
        label: "Outstanding",
        value: formatCurrency(summary.outstanding),
        theme: "warning" as const,
        icon: <Clock3 className="h-5 w-5" />,
      },
      {
        label: "Invoices",
        value: summary.entries,
        theme: "danger" as const,
        icon: <Wallet className="h-5 w-5" />,
      },
    ],
    [summary]
  );

  const tableHeaders = [
    "Invoice No",
    "Insurer",
    "Period",
    "Invoice",
    "Total Collected",
    "Outstanding",
    "Status",
    "Due Date",
  ];

  const tableHeaderKeys = [
    "invoice",
    "insurer",
    "issue_date",
    "commission",
    "total_collected",
    "outstanding",
    "status",
    "due_date",
  ];

  const insurerOptionsById = useMemo(
    () =>
      (insurers || [])
        .map((item) => ({
          value: item?.id,
          label: item?.name,
        }))
        .filter((option) => option.value !== null && option.value !== undefined),
    [insurers]
  );

  const statusOptions = useMemo(
    () =>
      (statuses || [])
        .map((item) => ({
          value: item?.id,
          label: item?.name,
        }))
        .filter((option) => option.value !== null && option.value !== undefined),
    [statuses]
  );

  const filters = useMemo(() => {
    const statusFilter = {
      name: "status",
      valueKey: "status",
      requests: {
        method: "GET" as const,
        url: App_Url_New.ApiEndPoint.commissionStatus,
        reducer: setCommissionStatuses,
      },
      options: statusOptions,
      placeholder: "Select Status",
      width: "170px",
    };

    if (B2C_FEATURE_FLAGS.isOCEnvironment) {
      return [statusFilter];
    }

    return [
      {
        name: "insurer_id",
        valueKey: "insurer_id",
        options: insurerOptionsById,
        placeholder: "Select Insurer",
        requests: {
          method: "GET" as const,
          url: App_Url_New.ApiEndPoint.commissionInsurer,
          reducer: setCommissionInsurer,
        },
        width: "170px",
      },
      statusFilter,
    ];
  }, [insurerOptionsById, statusOptions]);

  const renderTableRow = (row: InvoiceRecord) => {
    return (
      <>
        <td className="px-4 py-3 font-medium text-theme-text-soft">{row.invoiceNo}</td>
        <td className="px-4 py-3 max-w-[200px] truncate text-theme-text-soft" title={row.insurer}>
          {row.insurer}
        </td>
        <td className="px-4 py-3 max-w-40 truncate text-theme-text-soft" title={row.period}>
          {row.period}
        </td>
        <td className="px-4 py-3 text-theme-text-soft">{formatCurrency(row.invoiceAmount)}</td>
        <td className="px-4 py-3  text-theme-text-soft">{formatCurrency(row.totalCollected)}</td>
        <td
          className={`px-4 py-3 ${row.outstanding > 0 ? "text-theme-danger" : "text-theme-text-soft"}`}
        >
          {formatCurrency(row.outstanding)}
        </td>
        <td className="px-4 py-3">
          <Status status={textFormatter(row.status)} />
        </td>
        <td
          className="px-4 py-3 max-w-40 truncate text-theme-text-soft"
          title={formatDate(row.dueDate)}
        >
          {formatDate(row.dueDate)}
        </td>
      </>
    );
  };

  return (
    <>
      <div className="mb-3 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {summaryCards.map((item) => (
          <SummaryMetricCard
            key={item.label}
            title={item.label}
            value={item.value}
            theme={item.theme}
            icon={item.icon}
          />
        ))}
      </div>

      <DataTable
        headers={tableHeaders}
        headerKeys={tableHeaderKeys}
        rows={records}
        url={App_Url_New.ApiEndPoint.commissionLedger}
        listReducer={listReducer}
        renderRow={renderTableRow}
        onRowClick={openInvoicePrintModal}
        filters={filters}
        button={{
          name: "Generate Invoice",
          onClick: openGenerateInvoiceModal,
        }}
        searchPlaceholder="Search by Invoice No, Insurer, Customer etc..."
        isPagination={true}
        pageSize={10}
        totalCount={commissionRecords?.total || 0}
        sortable={true}
        staticHeight={60}
        nonSortableColumns={["Status", "Total Collected", "Outstanding", "Due Date"]}
      />
    </>
  );
}
